import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'
import { hasWorkstationSession, recoverWorkstationSession } from '@/services/workstationSession'

// 布局组件
const MainLayout = () => import('@/components/layout/MainLayout.vue')
const AuthLayout = () => import('@/components/layout/AuthLayout.vue')
const AdminLayout = () => import('@/components/layout/AdminLayout.vue')

// 页面组件
const Login = () => import('@/views/auth/Login.vue')
const Register = () => import('@/views/auth/Register.vue')
const ServiceAgreement = () => import('@/views/auth/ServiceAgreement.vue')
const PrivacyPolicy = () => import('@/views/auth/PrivacyPolicy.vue')
const LegalStatement = () => import('@/views/auth/LegalStatement.vue')
const WorkstationLogin = () => import('@/views/auth/WorkstationLogin.vue')

// 系统管理员页面
const AdminLogin = () => import('@/views/admin/AdminLogin.vue')
const AdminDashboard = () => import('@/views/admin/AdminDashboard.vue')
const AdminSettings = () => import('@/views/admin/AdminSettings.vue')
const Dashboard = () => import('@/views/Dashboard.vue')
const Quotations = () => import('@/views/quotations/QuotationList.vue')
const QuotationDetail = () => import('@/views/quotations/QuotationDetail.vue')
const QuotationCreate = () => import('@/views/quotations/QuotationCreate.vue')
const QuotationCreateEnhanced = () => import('@/views/quotations/QuotationCreateEnhanced.vue')
const QuotationWorkspace = () => import('@/views/quotations/QuotationWorkspace.vue')
const Customers = () => import('@/views/customers/CustomerList.vue')
const CustomerDetail = () => import('@/views/customers/CustomerDetail.vue')
const Rules = () => import('@/views/rules/RuleList.vue')
const RulesEnhanced = () => import('@/views/rules/RuleListEnhanced.vue')
const DXFUpload2 = () => import('@/views/dxf/DXFUpload2.vue')
const DXFAutoNesting = () => import('@/views/dxf/DXFAutoNesting.vue')
const NoDrawingRetired = () => import('@/views/no-drawing/NoDrawingRetired.vue')
const NoDrawingQuotation2 = () => import('@/views/no-drawing/NoDrawingQuotation2.vue')
const BasketDetailPage = () => import('@/views/no-drawing/BasketDetailPage.vue')
const ConsumablesQuotation = () => import('@/views/consumables/ConsumablesQuotation.vue')
const Settings = () => import('@/views/settings/Settings.vue')
const Profile = () => import('@/views/settings/SystemSettings.vue')
const SystemSetup = () => import('@/views/setup/SystemSetup.vue')

// 邀请管理页面
const InviteManagement = () => import('@/views/invites/InviteManagement.vue')
const EmployeeApproval = () => import('@/views/invites/EmployeeApproval.vue')
const EmployeeOnboarding = () => import('@/views/invites/EmployeeOnboarding.vue')

// 微信回调页面
const WechatCallback = () => import('@/views/WechatCallback.vue')

const routes = [
  {
    path: '/auth',
    component: AuthLayout,
    redirect: '/auth/login',
    children: [
      {
        path: 'login',
        name: 'Login',
        component: Login,
        meta: {
          title: '登录',
          requiresAuth: false,
          noTab: true // 登录页不显示标签
        }
      },
      {
        path: 'register',
        name: 'Register',
        component: Register,
        meta: {
          title: '注册',
          requiresAuth: false
        }
      },
      {
        path: 'service-agreement',
        name: 'ServiceAgreement',
        component: ServiceAgreement,
        meta: {
          title: '智能报价系统服务协议',
          requiresAuth: false
        }
      },
      {
        path: 'privacy-policy',
        name: 'PrivacyPolicy',
        component: PrivacyPolicy,
        meta: {
          title: '隐私权政策',
          requiresAuth: false
        }
      },
      {
        path: 'legal-statement',
        name: 'LegalStatement',
        component: LegalStatement,
        meta: {
          title: '法律声明',
          requiresAuth: false
        }
      }
    ]
  },
  {
    path: '/workstation-login',
    name: 'WorkstationLogin',
    component: WorkstationLogin,
    meta: {
      title: '工作台登录',
      requiresAuth: false
    }
  },
  {
    path: '/adminabc',
    component: AdminLayout,
    redirect: '/adminabc/dashboard',
    meta: {
      requiresAuth: true,
      requiresRole: ['system_admin']
    },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: AdminDashboard,
        meta: {
          title: '管理仪表盘',
          requiresRole: ['system_admin']
        }
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: AdminSettings,
        meta: {
          title: '系统设置',
          requiresRole: ['system_admin']
        }
      },
      {
        path: 'tenants',
        name: 'AdminTenants',
        component: () => import('@/views/admin/AdminTenants.vue'),
        meta: {
          title: '租户管理',
          requiresRole: ['system_admin']
        }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/admin/AdminUsers.vue'),
        meta: {
          title: '用户管理',
          requiresRole: ['system_admin']
        }
      },
      {
        path: 'pricing-settings',
        name: 'AdminPricingSettings',
        component: () => import('@/views/admin/AdminPricingSettings.vue'),
        meta: {
          title: '报价设置',
          requiresRole: ['system_admin']
        }
      },
      {
        path: 'logs',
        name: 'AdminLogs',
        component: () => import('@/views/admin/AdminLogs.vue'),
        meta: {
          title: '系统日志',
          requiresRole: ['system_admin']
        }
      },
      {
        path: 'security',
        name: 'AdminSecurity',
        component: () => import('@/views/admin/AdminSecurity.vue'),
        meta: {
          title: '安全设置',
          requiresRole: ['system_admin']
        }
      }
    ]
  },
  {
    path: '/adminabc/login',
    name: 'AdminLogin',
    component: AdminLogin,
    meta: {
      title: '系统管理员登录',
      requiresAuth: false
    }
  },
  {
    path: '/',
    component: MainLayout,
    redirect: '/dashboard',
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
          title: '工作台',
          icon: 'House',
          affix: true // 固定标签，不可关闭
        }
      },
      {
        path: 'redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect.vue'),
        meta: {
          title: '重定向',
          noTab: true // 重定向页面不显示标签
        }
      },
      {
        path: 'quotations',
        name: 'Quotations',
        component: Quotations,
        meta: {
          title: '报价单',
          icon: 'Document'
        }
      },
      {
        path: 'quotations/create',
        name: 'QuotationCreate',
        component: QuotationCreateEnhanced,
        meta: {
          title: '智能报价创建',
          parent: 'Quotations'
        }
      },
      {
        path: 'quotations/:id',
        name: 'QuotationDetail',
        component: QuotationDetail,
        meta: {
          title: '报价详情',
          parent: 'Quotations'
        }
      },
      {
        path: 'quotations/:id/workspace',
        name: 'QuotationWorkspace',
        component: QuotationWorkspace,
        meta: {
          title: '交互式报价编辑器',
          parent: 'Quotations'
        }
      },
      {
        path: 'customers',
        name: 'Customers',
        component: Customers,
        meta: {
          title: '客户列表',
          icon: 'User'
        }
      },
      {
        path: 'customers/:id',
        name: 'CustomerDetail',
        component: CustomerDetail,
        meta: {
          title: '客户详情',
          parent: 'Customers'
        }
      },
      {
        path: 'rules',
        name: 'Rules',
        component: RulesEnhanced,
        meta: {
          title: '报价设置',
          icon: 'TrendCharts',
          requiresRole: ['admin', 'operator', 'system_admin']
        }
      },
      {
        path: 'rules/legacy',
        name: 'RulesLegacy',
        component: Rules,
        meta: {
          title: '传统规则管理',
          parent: 'Rules'
        }
      },
      {
        path: 'rules/test',
        name: 'RuleEditTest',
        component: () => import('@/views/rules/test/RuleEditTest.vue'),
        meta: {
          title: '在位编辑测试',
          parent: 'Rules'
        }
      },
      {
        path: 'dxf/upload',
        name: 'DXFUpload',
        redirect: to => ({ path: '/dxf/upload2', query: to.query, hash: to.hash }),
        meta: {
          title: '旧文件报价',
          icon: 'Upload',
          hidden: true
        }
      },
      {
        path: 'dxf/upload2',
        name: 'DXFUpload2',
        component: DXFUpload2,
        meta: {
          title: '文件报价',
          icon: 'Upload'
        }
      },
      {
        path: 'dxf/auto-nesting',
        name: 'DXFAutoNesting',
        component: DXFAutoNesting,
        meta: {
          title: '自动拼版（训练）',
          icon: 'Grid',
          requiresRole: ['admin', 'operator']
        }
      },
      {
        path: 'no-drawing',
        name: 'NoDrawingQuotation',
        component: NoDrawingRetired,
        meta: { title: '旧参数报价已停用' }
      },
      {
        path: 'no-drawing2',
        name: 'NoDrawingQuotation2',
        component: NoDrawingQuotation2,
        meta: {
          title: '新参数报价',
          icon: 'Tools'
        }
      },
      {
        path: 'basket-detail',
        name: 'BasketDetail',
        component: BasketDetailPage,
        meta: {
          title: '报价篮',
          icon: 'ShoppingCart',
          requiresAuth: true
        }
      },
      {
        path: 'consumables',
        name: 'ConsumablesQuotation',
        component: ConsumablesQuotation,
        meta: {
          title: '耗材报价',
          icon: 'Box'
        }
      },
      {
        path: 'invites',
        name: 'Invites',
        component: InviteManagement,
        meta: {
          title: '邀请码管理',
          icon: 'Invitation',
          requiresRole: ['admin']
        }
      },
      {
        path: 'employees/approval',
        name: 'EmployeeApproval',
        component: EmployeeApproval,
        meta: {
          title: '员工审核',
          icon: 'UserFilled',
          requiresRole: ['admin']
        }
      },
      {
        path: 'onboarding',
        name: 'EmployeeOnboarding',
        component: EmployeeOnboarding,
        meta: {
          title: '员工入驻',
          requiresAuth: false
        }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: Settings,
        meta: {
          title: '系统设置',
          icon: 'Setting',
          requiresRole: ['admin']
        },
        children: [
          {
            path: 'cad-layer-rules',
            name: 'CadLayerRules',
            component: () => import('@/views/settings/CadLayerRules.vue'),
            meta: { title: 'CAD图层规则', requiresRole: ['admin'] }
          },
          {
            path: 'setup',
            name: 'SystemSetup',
            component: SystemSetup,
            meta: {
              title: '系统初始化',
              requiresRole: ['admin']
            }
          },
          {
            path: 'organizations',
            name: 'OrganizationManagement',
            component: () => import('@/views/settings/OrganizationManagement.vue'),
            meta: {
              title: '组织管理',
              requiresRole: ['admin']
            }
          },
          {
            path: 'employees',
            name: 'EmployeeManagement',
            component: () => import('@/views/settings/EmployeeManagement.vue'),
            meta: {
              title: '员工管理',
              requiresRole: ['admin']
            }
          },
          {
            path: 'org-config',
            name: 'OrganizationConfig',
            component: () => import('@/views/settings/OrganizationConfig.vue'),
            meta: {
              title: '组织配置',
              requiresRole: ['admin']
            }
          }
        ]
      },
      {
        path: 'profile',
        name: 'Profile',
        component: Profile,
        meta: {
          title: '个人设置',
          icon: 'Setting'
        }
      },
      {
        path: 'profile/enterprise',
        name: 'EnterpriseSettings',
        component: Profile,
        meta: {
          title: '企业设置',
          requiresRole: ['admin']
        }
      },
      {
        path: 'profile/ai',
        name: 'AISettings',
        component: Profile,
        meta: {
          title: 'AI设置',
          requiresRole: ['admin']
        }
      }
    ]
  },
  {
    path: '/setup',
    name: 'InitialSetup',
    component: SystemSetup,
    meta: {
      title: '系统初始化',
      requiresAuth: false
    }
  },
  {
    path: '/auth/wechat-callback',
    name: 'WechatCallback',
    component: WechatCallback,
    meta: {
      title: '微信授权',
      requiresAuth: false
    }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: {
      title: '页面不存在'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 全局路由守卫 - 生产环境认证逻辑
router.beforeEach(async (to, from, next) => {
  // 设置页面标题
  if (to.meta?.title) {
    document.title = `${to.meta.title} - 智能报价管理系统`
  }

  const userStore = useUserStore()
  let token = localStorage.getItem('token')

  // 不需要认证的路由
  const publicRoutes = ['/auth/login', '/auth/register', '/auth/service-agreement', '/auth/privacy-policy', '/auth/legal-statement', '/setup', '/adminabc/login', '/auth/wechat-callback']

  if (publicRoutes.includes(to.path) || to.meta?.requiresAuth === false) {
    next()
    return
  }

  // 系统管理员路由特殊处理
  const isAdminRoute = to.path.startsWith('/adminabc')

  // 需要认证的路由检查token
  if (!token) {
    if (hasWorkstationSession()) {
      try { await recoverWorkstationSession(userStore); token = localStorage.getItem('token'); }
      catch { ElMessage.warning('工作台会话暂不可用，请稍后重试'); next(false); return; }
    }
  }
  if (!token) {
    console.log(`未登录访问 ${to.path}，重定向到登录页`)
    if (isAdminRoute) {
      next('/adminabc/login')
    } else {
      next('/auth/login')
    }
    return
  }

  // 验证token有效性
  try {
    // 总是验证 token（即使有缓存的 user 也要验证，因为 token 可能已过期）
    await userStore.validateToken()

    // 系统管理员路由额外验证
    if (isAdminRoute) {
      if (userStore.user?.role !== 'system_admin') {
        console.warn(`非系统管理员尝试访问管理路径: ${to.path}`)
        ElMessage.error('仅限系统管理员访问')
        await userStore.logout()
        next('/adminabc/login')
        return
      }
    }

    // 检查角色权限
    if (to.meta?.requiresRole) {
      const requiredRoles = to.meta.requiresRole
      const userRole = userStore.user?.role

      // 如果用户角色不在允许的角色列表中，拒绝访问
      if (!requiredRoles.includes(userRole)) {
        console.warn(`用户角色 "${userRole}" 无权访问 "${to.path}"，仅允许: ${requiredRoles.join(', ')}`)
        ElMessage.error(`无权访问此页面，仅限 ${requiredRoles.join('、')} 角色`)

        // 根据用户类型返回相应页面
        if (userRole === 'system_admin') {
          next('/adminabc/dashboard')
        } else {
          next('/dashboard')
        }
        return
      }
    }

    next()
  } catch (error) {
    console.error('token验证失败:', error)
    if (hasWorkstationSession()) {
      ElMessage.warning('工作台会话或权限暂不可用，请稍后重试')
      next(false)
      return
    }
    // 清除过期的认证信息
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('loginTime')
    await userStore.logout()

    ElMessage.error('登录已过期，请重新登录')

    // 根据目标路由返回相应登录页
    if (isAdminRoute) {
      next('/adminabc/login')
    } else {
      next('/auth/login')
    }
  }
})

// 路由错误处理
router.onError((error) => {
  console.error('路由错误:', error)
  ElMessage.error('页面加载失败')
})

export default router
